import React from 'react';
import { DataAPIContext, GET_LIST, CREATE } from '../api';
import IconButton from '@mui/material/IconButton';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CopyIcon from '@mui/icons-material/FileCopyOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsIcon from '@mui/icons-material/Sms';
import ChatIcon from '@mui/icons-material/Chat';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import SyncIcon from '@mui/icons-material/Sync';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { LiveHelpIcon as OtherDeliveryIcon } from '@mui/icons-material/LiveHelp';
import BookingActionsMenu from './BookingActionsMenu';
import { isBackgroundTaskResponse, notifyBackgroundTask } from '../bg_tasks/batchResponseHelper';
import { BackgroundTaskContext } from '../bg_tasks/BackgroundTaskProvider';

import config from '../config';

import RecordListPro from '../components/RecordListPro'; // See TripUserManager reference for using RecordListPro
import { ContextStore } from '../store/contextStore';
import { Grid, Typography, Tooltip, Chip, Popover, Paper, Box, Skeleton, Stack, TableHead, TableBody, TableRow, TableCell, Table, TableSortLabel, Alert, Divider } from '@mui/material';
import ImportPreview from '../xml_import/ImportPreview';
import {
    bookingStatesGridOptions, sourcesGridOptions, checkinSourcesGridOptions, surveyStatesGridOptions,
    CHECKIN_SOURCES, SURVEY_STATES, SOURCES,
    BOOKING_STATES, BOOKING_STATE_BOOKED, BOOKING_STATE_CANCELLED, BOOKING_STATE_ON_HOLD, BOOKING_STATE_CANCELLED_ON_HOLD,
    precheckinStatusesGridOptions, PRECHECKIN_STATUSES,
} from '../trips/UserManager/utils';
import { ORIGINS, getImportStateDisplay } from '../xml_import/utils';
import { green, red, yellow, grey } from '@mui/material/colors';
import { Link } from '@mui/material';
import moment from 'moment';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/system';
import { useIsWidthUp, copyTextToClipboard, generateCWLink } from '../utils';
import { useSnackbar } from 'notistack';
import { EVENT_DELIVERY_STATES } from '../notifications/utils';
import { EventStatusCell, methodCounts, sandboxCountsLine, sandboxMethodLabel } from '../notifications/deliveryStatus';

const deliveryPalette = [
    '#7b1e28',
    '#261020',
    '#f4e3b2',
    '#091c3d',
    '#cca000',
];

const useStyles = makeStyles((theme) => ({
    root: {
        margin: theme.spacing(2),
        marginBottom: theme.spacing(0),
        display: 'flex',
        height: `calc(${theme.cp.contentHeight} - 9rem)`,
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 11rem)`,
            margin: 0,
        },
    },
    recordListWrapper: {
        height: `calc(${theme.cp.contentHeight} - 10rem) !important`,
        [theme.breakpoints.down('sm')]: {
            height: `100% !important`,
            marginTop: '-0.5rem !important',
        },
    },
    recordList: {
        width: '100%',
        borderRadius: '0 0 15px 15px !important',
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
        },
    },
    importTableHeadCell: {
        fontWeight: 'bold',
    },
    importTableCell: {
        fontSize: '0.5em',
    },
}));

const tagSortFn = (a, b) => {
    let res = a.category.name.localeCompare(b.category.name);
    if (res === 0) {
        res = a.name.localeCompare(b.name);
    }
    return res;
}

const BookingStatusChip = ({ value }) => {
    const theme = useTheme();
    const label = BOOKING_STATES[value] || 'Unknown';
    let bst_color = grey[600];
    let bst_variant = "filled";
    switch (value) {
        case BOOKING_STATE_BOOKED: bst_color = green[600]; break;
        case BOOKING_STATE_CANCELLED: bst_color = red[600]; bst_variant = "outlined"; break;
        case BOOKING_STATE_ON_HOLD: bst_color = yellow[600]; break;
        case BOOKING_STATE_CANCELLED_ON_HOLD: bst_color = red[600]; bst_variant = "outlined"; break;
        default: bst_color = grey[600]; bst_variant = "outlined"; break;
    }
    return <Chip
        label={label}
        style={{
            backgroundColor: bst_color,
            color: theme.palette.getContrastText(bst_color)
        }}
        variant={bst_variant}
        size="small"
    />;
};

const ColoredTagCell = (props) => {
    const theme = useTheme();
    const { tagColor } = React.useContext(ContextStore);

    return (<>{props.tags.toSorted(tagSortFn).map((tag, idx, lst) => {
        const color = tagColor(tag);
        return <Tooltip key={idx} title={tag.category?.name ?? '-'}><Chip
            style={{
                backgroundColor: color,
                color: theme.palette.getContrastText(color),
            }}
            variant="filled"
            label={tag.name}
            size="small"
        /></Tooltip>
    })}</>);
};

const DateTimeCell = (props) => {
    const mdt = props.value ? moment.tz(props.value, props.tz || 'UTC') : null;
    return <Typography variant="body2" fontSize="0.75rem">{mdt ? mdt.format('MMM DD, YYYY HH:mm z') : ''}</Typography>;
};

const CopyableProperty = ({ value, label }) => {
    const { enqueueSnackbar } = useSnackbar();
    const copyToClipboard = (text, label) => {
        if (text) {
            copyTextToClipboard(text);
            enqueueSnackbar(label ? `${label} copied!` : 'Copied!', { variant: 'success', autoHideDuration: 500 });
        } else {
            enqueueSnackbar(label ? `${label} is empty!` : 'Cannot copy empty string!', { variant: 'error', autoHideDuration: 500 });
        }
    };
    return <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body1" component="span" style={{ fontWeight: 'bold' }}>{label}</Typography>
        {value && value.toString().length && <>
            <Typography variant="body2" component="span" style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{value}</Typography>
            <Tooltip title="Copy to clipboard">
                <IconButton onClick={() => copyToClipboard(value, label)}><CopyIcon fontSize='small' /></IconButton>
            </Tooltip>
        </>}
    </Stack>;
}

const LinkableProperty = ({ value, href, label, target = '_blank' }) => {
    return <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body1" component="span" style={{ fontWeight: 'bold' }}>{label}</Typography>
        {target && value && (<>
            <Link href={href} target={target}>{value}</Link>
            {target === '_blank' && <OpenInNewIcon fontSize="x-small" />}
        </>)}
    </Stack>
}

const PropertyList = ({ properties }) => {
    return <Stack direction="row" spacing={1} alignItems="center">
        {properties.map((p, idx) => {
            const Component = p.Component ? p.Component : StaticProperty;
            return <Component key={idx} {...p} />
        })}
    </Stack>
}

const StaticProperty = ({ value, label }) => {
    return <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body1" component="span" style={{ fontWeight: 'bold' }}>{label}</Typography>
        <Typography variant="body2" component="span">{value}</Typography>
    </Stack>
}

const getIconForMethod = (method) => {
    switch (method) {
        case 'email': return <EmailIcon fontSize="small" />;
        case 'whatsapp': return <WhatsAppIcon fontSize="small" />;
        case 'sms': return <SmsIcon fontSize="small" />;
        case 'chat': return <ChatIcon fontSize="small" />;
        default: return <OtherDeliveryIcon fontSize="small" />;
    }
}
const NotificationDeliveryList = ({ ndas }) => {
    const theme = useTheme();
    const list = [];
    ndas.forEach((nda, idx) => {
        const color = deliveryPalette[idx % deliveryPalette.length];
        nda.methods.toSorted((a, b) => (+a.delivery_method?.sequence || 0) - (+b.delivery_method?.sequence || 0)).forEach((m) => {
            list.push({
                color,
                icon: getIconForMethod(m.delivery_method?.method),
                notification_name: nda.notification?.name ?? '-',
                notification_id: nda.notification?.id ?? '-',
                method_id: m.delivery_method?.id ?? '-',
                method: m.delivery_method?.method_name ?? '-',
                n_errors: nda.n_error,
                n_success: nda.n_success,
                status: m.status,
            });
        });
    });
    return <>{list.map((item) => (
        <Chip
            {...(sandboxMethodLabel(item.status) ? { icon: <ScienceOutlinedIcon sx={{ fontSize: 14 }} /> } : {})}
            label={<Link
                href={`/notifications/${item.notification_id}/${item.method_id}`}
                target="_blank"
                style={{ textDecoration: 'none', color: 'inherit' }}
            >{`${item.notification_id} | ${item.method}: ${methodCounts(item.status, item.n_success, item.n_errors)}`}

            </Link>}
            title={`${item.notification_name} [${item.notification_id}] | ${item.method}: ${sandboxMethodLabel(item.status) ? `${sandboxMethodLabel(item.status)}, ` : ''}${item.n_success} successful deliveries, ${item.n_errors} delivery errors`}
            style={{
                backgroundColor: item.color,
                color: theme.palette.getContrastText(item.color)
            }}
            variant="outlined"
            size="small"
        />
    ))
    }</>;
};

const getIconForDeliveryMethod = (deliveryMethod) => {
    switch (deliveryMethod) {
        case 0: return <EmailIcon fontSize="small" />;
        case 1: return <SmsIcon fontSize="small" />;
        case 3: return <WhatsAppIcon fontSize="small" />;
        case 4: return <ChatIcon fontSize="small" />;
        default: return null;
    }
};

const NotificationLogDeliveryList = ({ notifications }) => {
    const theme = useTheme();
    if (!notifications || notifications.length === 0) return null;
    return <>{notifications.map((nl, idx) => {
        const color = deliveryPalette[idx % deliveryPalette.length];
        // A sandbox's method (S4b statuses 6-8) carries the flask instead of the channel icon.
        const sandboxLabel = sandboxMethodLabel(nl.status);
        const icon = sandboxLabel ? <ScienceOutlinedIcon sx={{ fontSize: 14 }} /> : getIconForDeliveryMethod(nl.delivery_method);
        return <Chip
            key={nl.id}
            {...(icon ? { icon } : {})}
            label={<Link
                href={`/notifications/${nl.notification_id}`}
                target="_blank"
                style={{ textDecoration: 'none', color: 'inherit' }}
            >{`${nl.notification_id} | ${nl.delivery_method_name}: ${methodCounts(nl.status, nl.n_success, nl.n_error)}`}</Link>}
            title={`${nl.notification_name} [${nl.notification_id}] via ${nl.delivery_method_name}: ${sandboxLabel ? `${sandboxLabel}, ` : ''}${nl.n_success} ok, ${nl.n_error} err`}
            style={{
                backgroundColor: color,
                color: theme.palette.getContrastText(color)
            }}
            variant="outlined"
            size="small"
        />;
    })}</>;
};

const EventDeliveryList = ({ participant, enableAdminLinks = false, enableCWLinks = false }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [eventDeliveries, setEventDeliveries] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    React.useEffect(() => {
        dataAPI(GET_LIST, 'tour_user', { urlSuffix: `${participant}/event_delivery_attempts/` }).then((res) => {
            setEventDeliveries(res);
            setLoading(false);
        });
    }, [participant]);
    if (loading) {
        return <Skeleton variant="rectangular" height={100} />;
    }
    if (eventDeliveries.length === 0) {
        return <><h4>Event delivery attempts</h4><Typography variant="body2">No event delivery attempts found</Typography></>;
    }
    const attemptCounts = sandboxCountsLine(eventDeliveries.map((d) => d.status));
    return (<>
        <h4>Event delivery attempts</h4>
        {attemptCounts && <Typography variant="body2" color="text.secondary" data-testid="sandbox-counts">{attemptCounts}</Typography>}
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Log ID</TableCell>
                    <TableCell>Time<TableSortLabel active direction="desc" /></TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Deliveries</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {eventDeliveries.map((delivery, idx) => {
                    const log_id = [delivery.log_id, (delivery.log_tag ? delivery.log_tag : '')].filter(Boolean).join('/');
                    const cwLink = generateCWLink(
                        moment.utc(delivery.created_ts).subtract(1, 'minutes').toISOString(),
                        moment.utc(delivery.created_ts).add(1, 'hours').toISOString(),
                        `fields @timestamp, @message, @logStream, @log | filter message like /${delivery.log_id}/ | sort @timestamp asc | display @timestamp, coalesce(message, @message)`
                    );
                    return (<TableRow key={idx}>
                        <TableCell>{enableAdminLinks ?
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Link href={`${config.adminBaseUrl}/admin/demo/event_delivery_attempt/${delivery.id}/change/`} target="_blank">{delivery.id}</Link>
                                <OpenInNewIcon fontSize="x-small" />
                            </Stack> : delivery.id}</TableCell>
                        <TableCell>{delivery.event}</TableCell>
                        <TableCell>{enableCWLinks ? <>
                            <Link href={cwLink} target="_blank">{log_id}</Link>
                            <OpenInNewIcon fontSize="x-small" />
                        </> : <>{log_id}</>}
                        </TableCell>
                        <TableCell>
                            <Tooltip title={`Created at ${moment.utc(delivery.created_ts).format('YYYY-MM-DD HH:mm:ss')}Z} | Processed in ${moment.duration(delivery.duration, 'seconds').asSeconds()} s`}>
                                {moment.utc(delivery.created_ts).format('YYYY-MM-DD HH:mm:ss')}Z | {moment.duration(delivery.duration, 'seconds').asSeconds()} s
                            </Tooltip>
                        </TableCell>
                        <TableCell><EventStatusCell status={delivery.status} fallback={EVENT_DELIVERY_STATES[delivery.status]} /></TableCell>
                        <TableCell><NotificationDeliveryList ndas={delivery.notifications} /></TableCell>
                    </TableRow>)
                })}
            </TableBody>
        </Table>
        </Box>
    </>);
};

const EventDeliveryLogList = ({ participant, enableAdminLinks = false, enableCWLinks = false }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [deliveryLogs, setDeliveryLogs] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    React.useEffect(() => {
        dataAPI(GET_LIST, 'tour_user', { urlSuffix: `${participant}/event_delivery_logs/` }).then((res) => {
            setDeliveryLogs(res);
            setLoading(false);
        });
    }, [participant]);
    if (loading) {
        return <Skeleton variant="rectangular" height={100} />;
    }
    if (deliveryLogs.length === 0) {
        return <><h4>Event delivery logs</h4><Typography variant="body2">No event delivery logs found</Typography></>;
    }
    const logCounts = sandboxCountsLine(deliveryLogs.map((dl) => dl.status));
    return (<>
        <h4>Event delivery logs</h4>
        {logCounts && <Typography variant="body2" color="text.secondary" data-testid="sandbox-counts">{logCounts}</Typography>}
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Log ID</TableCell>
                    <TableCell>Time<TableSortLabel active direction="desc" /></TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Notifications</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {deliveryLogs.map((dl) => {
                    const log_id = [dl.log_id, (dl.log_tag ? dl.log_tag : '')].filter(Boolean).join('/');
                    const cwLink = generateCWLink(
                        moment.utc(dl.created_ts).subtract(1, 'minutes').toISOString(),
                        moment.utc(dl.created_ts).add(1, 'hours').toISOString(),
                        `fields @timestamp, @message, @logStream, @log | filter message like /${dl.log_id}/ | sort @timestamp asc | display @timestamp, coalesce(message, @message)`
                    );
                    const shortId = typeof dl.id === 'string' ? dl.id.split('-').pop() : dl.id;
                    return (<TableRow key={dl.id}>
                        <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                {enableAdminLinks ?
                                    <Tooltip title={dl.id}>
                                        <Link href={`${config.adminBaseUrl}/admin/demo/event_delivery_log/${dl.id}/change/`} target="_blank">
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{shortId}</Typography>
                                        </Link>
                                    </Tooltip>
                                :
                                    <Tooltip title={dl.id}>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{shortId}</Typography>
                                    </Tooltip>
                                }
                                <IconButton size="small" onClick={() => { copyTextToClipboard(dl.id) }} title="Copy full ID">
                                    <CopyIcon sx={{ fontSize: '0.75rem' }} />
                                </IconButton>
                            </Stack>
                        </TableCell>
                        <TableCell>{dl.event}</TableCell>
                        <TableCell>{enableCWLinks ? <>
                            <Link href={cwLink} target="_blank">{log_id}</Link>
                            <OpenInNewIcon fontSize="x-small" />
                        </> : <>{log_id}</>}
                        </TableCell>
                        <TableCell>
                            <Tooltip title={`Emitted: ${moment.utc(dl.created_ts).format('YYYY-MM-DD HH:mm:ss')}Z | Finalized: ${dl.finalized_ts ? moment.utc(dl.finalized_ts).format('YYYY-MM-DD HH:mm:ss') + 'Z' : '-'} | Duration: ${dl.duration_ms} ms`}>
                                <span>{moment.utc(dl.created_ts).format('YYYY-MM-DD HH:mm:ss')}Z | {dl.duration_ms} ms</span>
                            </Tooltip>
                        </TableCell>
                        <TableCell><EventStatusCell status={dl.status} fallback={EVENT_DELIVERY_STATES[dl.status] || dl.status_display} nCaptured={dl.n_captured} /></TableCell>
                        <TableCell><NotificationLogDeliveryList notifications={dl.notifications} /></TableCell>
                    </TableRow>)
                })}
            </TableBody>
        </Table>
        </Box>
    </>);
};

const CacheRecordList = ({ participant, user, enableCWLinks = false }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [cacheRecords, setCacheRecords] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    React.useEffect(() => {
        dataAPI(GET_LIST, 'tour_user', { urlSuffix: `${participant}/cache_records/` }).then((res) => {
            setCacheRecords(res);
            setLoading(false);
        });
    }, [participant]);

    if (loading) {
        return <Skeleton variant="rectangular" height={100} />;
    }
    if (cacheRecords.length === 0) {
        return <><h4>Cache records</h4><Typography variant="body2">No cache records found</Typography></>;
    }
    return (<>
        <h4>Cache records</h4>
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Object</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Log ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>CDN</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {cacheRecords.map((record, idx) => {
                    const cwLink = generateCWLink(
                        moment.utc(record.timestamp).subtract(1, 'minutes').toISOString(),
                        moment.utc(record.timestamp).add(1, 'hours').toISOString(),
                        `fields @timestamp, @message, @logStream, @log | filter message like /${record.logid}/ | sort @timestamp asc | display @timestamp, coalesce(message, @message)`
                    );
                    const userMismatch = record.user.id !== user.id;
                    return (<TableRow key={idx}>
                        <TableCell sx={{ verticalAlign: 'top' }}>{record.id}</TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>{record.version}</TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>{moment.utc(record.timestamp).format('MMM DD, YYYY HH:mm:ss')}Z</TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>{record.api_type}.{record.object_id}</TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                            <Link href={`/user/${record.user.id}`} target="_blank">{record.user.email}</Link> <OpenInNewIcon fontSize="x-small" />
                            {userMismatch && <Alert severity="error">Different from booking (<Link href={`/user/${user.id}`} target="_blank">{user.email}</Link>).</Alert>}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>{enableCWLinks ? <>
                            <Link href={cwLink} target="_blank">{record.logid}</Link>
                            <OpenInNewIcon fontSize="x-small" />
                        </> : <>{record.logid}</>}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>{record.is_deleted ? 'Deleted' : 'In cache'}</TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                            {record.url && <Link href={record.url} target="_blank"><FileDownloadOutlinedIcon fontSize="small" /></Link>}
                            {!record.url && <Typography variant="body2">N/A</Typography>}
                        </TableCell>
                    </TableRow>)
                })}
            </TableBody>
        </Table>
        </Box>
    </>);
}

const ImportTableRow = ({ run, onOpenImportDetails, enableCWLinks = false }) => {
    const IMPORT_DETAILS_TAB_INPUT = 1;
    const IMPORT_DETAILS_TAB_LOGS = 2;
    const dataAPI = React.useContext(DataAPIContext);
    const [runIndexes, setRunIndexes] = React.useState({});
    const [loading, setLoading] = React.useState(false);
    React.useEffect(() => {
        if (!loading) { return; }
        if (runIndexes[run.id]) {
            setLoading(false);
            return;
        }
        dataAPI(GET_LIST, 'import_run', { urlSuffix: `${run.id}/import_run_index/` }).then((res) => {
            setRunIndexes(prev => ({
                ...prev,
                [run.id]: res.data,
            }));
            setLoading(false);
        }).catch((err) => {
            console.error(err);
            setLoading(false);
        });
    }, [run, loading]);

    const cwLink = generateCWLink(
        moment.utc(run.created_ts).subtract(1, 'minutes').toISOString(),
        moment.utc(run.created_ts).add(1, 'hours').toISOString(),
        `fields @timestamp, @message, @logStream, @log | filter request_id == "${run.request_id}" | sort @timestamp asc | display @timestamp, coalesce(message, @message)`
    );
    const [anchorEl, setAnchorEl] = React.useState(null);
    const handlePopoverOpen = (event) => {
        setAnchorEl(event.currentTarget);
        setLoading(true);
    };
    const handlePopoverClose = () => {
        setAnchorEl(null);
        setLoading(false);
    };
    const groupOrigin = run.group_run ? ORIGINS[run.group_run.origin] : null;
    return (<TableRow >
        <TableCell>
            <Link
                variant="text"
                href="javascript:void(0)"
                onClick={() => onOpenImportDetails(run, IMPORT_DETAILS_TAB_INPUT)}
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}>
                {run.id}
            </Link>
            &nbsp;
            <Link variant="text" href="javascript:void(0)" onClick={() => onOpenImportDetails(run, IMPORT_DETAILS_TAB_LOGS)} title="Import logs"><ManageSearchIcon fontSize="x-small" /></Link>
            &nbsp;
            <Link href={`/import/run/list/${run.id}`} target="_blank" title="Open in new tab"><OpenInNewIcon fontSize="x-small" /></Link>
            <Popover
                sx={{ pointerEvents: 'none' }}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClose={handlePopoverClose}
                disableRestoreFocus>
                <Paper>
                    <Box p={2}>
                        {loading && <>
                            <Skeleton variant="text" height={15} width={250} animation="wave" />
                            <Skeleton variant="text" height={15} width={250} animation="wave" />
                            <Skeleton variant="text" height={15} width={250} animation="wave" />
                            <Skeleton variant="text" height={15} width={250} animation="wave" />
                        </>}
                        {!loading && runIndexes[run.id] && runIndexes[run.id].length === 0 && <Typography variant="body2">No indexes found</Typography>}
                        {!loading && runIndexes[run.id] && runIndexes[run.id].length > 0 && <>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Affected records</Typography>
                            <Table size="small" dense sx={{ fontSize: '0.875rem' }}>
                                <TableBody>
                                    {runIndexes[run.id].toSorted((a, b) => a.type_display.localeCompare(b.type_display)).map((index, idx) => <TableRow key={idx}>
                                        <TableCell>{index.type_display}</TableCell>
                                        <TableCell>{index.value}</TableCell>
                                    </TableRow>)}
                                </TableBody>
                            </Table>
                        </>}
                    </Box>
                </Paper>
            </Popover>
        </TableCell>
        <TableCell>
            {run.group && (<>
                <Link href={`/import/run/list/${run.group}`} target="_blank">{run.group}</Link> <OpenInNewIcon fontSize="x-small" />
            </>)}
        </TableCell>
        <TableCell>{enableCWLinks ? <>
            <Link href={cwLink} target="_blank">{run.request_id}</Link>
            <OpenInNewIcon fontSize="x-small" />
        </> : <>{run.request_id}</>}
        </TableCell>
        <TableCell>{moment.utc(run.created_ts).format('MMM DD, YYYY HH:mm:ss')}Z</TableCell>
        <TableCell>{ORIGINS[run.origin]}{groupOrigin ? ` <- ${groupOrigin}` : ''}</TableCell>
        <TableCell>{getImportStateDisplay(run.state)}</TableCell>
    </TableRow>)
}

const ImportList = ({ participant, enableCWLinks = false }) => {
    const dataAPI = React.useContext(DataAPIContext);

    const [imports, setImports] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [activeImport, setActiveImport] = React.useState(null);
    const [activeImportTab, setActiveImportTab] = React.useState(0);
    const onOpenImportDetails = (run, tab) => {
        setActiveImport(run);
        setActiveImportTab(tab);
    };
    const onImportPreviewClose = (ev) => {
        setActiveImport(null);
    };
    const onImportReimported = (run) => {
        setActiveImport(run);
    };
    const onRunClick = (run_id, ev) => {
        setActiveImport(run_id);
    };

    React.useEffect(() => {
        dataAPI(GET_LIST, 'tour_user', { urlSuffix: `${participant}/imports/` }).then((res) => {
            setImports(res);
            setLoading(false);
        });
    }, [participant]);

    if (loading) {
        return <Skeleton variant="rectangular" height={100} />;
    }
    if (imports.length === 0) {
        return <><h4>Import runs</h4><Typography variant="body2">No imports found</Typography></>;
    }
    return (<>
        <h4>Import runs</h4>
        {activeImport && <ImportPreview
            id={activeImport.id}
            data={activeImport}
            onClose={onImportPreviewClose}
            onImportReimported={onImportReimported}
            onRunClick={onRunClick}
            lastTabSelected={activeImportTab}
        />}
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Import</TableCell>
                    <TableCell>Group</TableCell>
                    <TableCell>API request</TableCell>
                    <TableCell>Timestamp<TableSortLabel active direction="desc" /></TableCell>
                    <TableCell>Origin</TableCell>
                    <TableCell>Status</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {imports.map((run, idx) => <ImportTableRow
                    key={idx}
                    run={run}
                    onOpenImportDetails={onOpenImportDetails}
                    enableCWLinks={enableCWLinks}
                />)}
            </TableBody>
        </Table>
        </Box>
    </>);
}

const RevisionList = ({ participant }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [versions, setVersions] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    React.useEffect(() => {
        dataAPI(GET_LIST, 'tour_user', { urlSuffix: `${participant}/list_revisions/` }).then((res) => {
            setVersions(res.data);
            setLoading(false);
        });
    }, [participant]);

    if (loading) {
        return <Skeleton variant="rectangular" height={100} />;
    }
    if (versions.length === 0) {
        return <><h4>Revisions</h4><Typography variant="body2">No revisions found</Typography></>;
    }
    return (<>
        <h4>Revisions</h4>
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Revision</TableCell>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Comment</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {versions.map((version, idx) => {
                    return (<TableRow key={idx}>
                        <TableCell>{version.revision.id}</TableCell>
                        <TableCell>{moment.utc(version.revision.date_created).format('MMM DD, YYYY HH:mm:ss')}Z</TableCell>
                        <TableCell>{version.revision.user?.email || '-'}</TableCell>
                        <TableCell>{version.revision.comment}</TableCell>
                    </TableRow>)
                })}
            </TableBody>
        </Table>
        </Box>
    </>);
};

const BookingDetailPanel = ({ row, enableAdminLinks = false, enableCWLinks = false }) => {
    const theme = useTheme();

    const startDate = moment.tz(row.start_datetime, row.tour.start_timezone).format('YYYY-MM-DD');
    const startDateFilter = `%7B"recordListFilters":%7B"dateStartFilter":"${startDate}"%7D%7D`;

    return (<div style={{ margin: theme.spacing(2), backgroundColor: grey[100], padding: theme.spacing(2), borderRadius: theme.spacing(1) }}>
        <Grid container direction="row" spacing={0}>
            <Grid item xs={12} sm={6}>
                <LinkableProperty
                    value={row.tour.name}
                    href={`/trip/${row.tour.id}`}
                    label="Tour"
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <PropertyList properties={[
                    // CP-8349: a guide group's dates are synthetic -- no departure to show.
                    row.tour?.is_staff_chat_group
                        ? { value: 'Guide group (no dates)', label: 'Departure' }
                        : {
                            Component: LinkableProperty,
                            value: `${moment.tz(row.start_datetime, row.tour.start_timezone).format('MMM DD, YYYY HH:mm z')}`,
                            href: `/trip/${row.tour.id}/users/manager/${row.id}/${startDateFilter}`,
                            label: 'Departure',
                        },
                    { value: row.departure?.name, label: '' },
                    { value: <BookingStatusChip value={row.booking_state} />, label: '' },
                    { value: row.id },
                ]} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <CopyableProperty value={row.custom_id} label="External ID" />
            </Grid>
            <Grid item xs={12} sm={6}>
                <CopyableProperty value={row.order_ref} label="Order #" />
            </Grid>
            <Grid item xs={12} sm={6}>
                <CopyableProperty value={row.departure_key} label="Departure code" />
            </Grid>
            <Grid item xs={12} sm={6}>
                <CopyableProperty value={row.supplier_ref} label="Supplier #" />
            </Grid>
            <Grid item xs={12} sm={6}>
                <PropertyList properties={[
                    { Component: CopyableProperty, value: row.reseller_ref, label: 'Reseller #' },
                    { Component: CopyableProperty, value: row.affiliate_ext_id, label: 'Affiliate #' },
                ]} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <PropertyList properties={[
                    { value: moment.utc(row.created_ts).format('MMM DD, YYYY HH:mm:ss Z'), label: 'Created at' },
                    { value: SOURCES[+row.create_source], label: 'by' },
                ]} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <PropertyList properties={[
                    { value: moment.utc(row.updated_ts).format('MMM DD, YYYY HH:mm:ss Z'), label: 'Updated at' },
                    { value: SOURCES[+row.update_source], label: 'by' },
                ]} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <LinkableProperty
                    value={row.branchio_link}
                    href={row.branchio_link}
                    label="Deep link"
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <PropertyList properties={[
                    { value: row.mytrips_display ? 'Yes' : 'No', label: 'My Trips' },
                    { value: row.exclude_from_chat ? 'Yes' : 'No', label: 'Chat disabled' },
                    { value: row.exclude_from_group_chat ? 'Yes' : 'No', label: 'Group Chat disabled' },
                ]} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <LinkableProperty
                    value={row.voucher?.name || row.voucher?.file}
                    href={row.voucher?.file}
                    label="Voucher"
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <LinkableProperty
                    value={row.main_booking?.display_name ?? 'link'}
                    href={row.main_booking ? `/trip/${row.tour.id}/users/manager/${row.main_booking.id}` : ''}
                    label="Main booking"
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <StaticProperty value={row.people_count} label="People count" />
            </Grid>
            <Grid item xs={12} sm={6}>
                <LinkableProperty
                    value={row.guide_sr_link}
                    href={row.guide_sr_link}
                    label="Guide self-registration link"
                />
            </Grid>
            <Grid item xs={12} mt={2}>
                <ColoredTagCell tags={row.tags} />
            </Grid>
            <Grid item xs={12}>
                <ImportList participant={row.id} enableCWLinks={enableCWLinks} />
            </Grid>
            <Grid item xs={12}>
                <CacheRecordList participant={row.id} user={row.user} enableCWLinks={enableCWLinks} />
            </Grid>
            <Grid item xs={12}>
                <EventDeliveryList participant={row.id} enableAdminLinks={enableAdminLinks} enableCWLinks={enableCWLinks} />
            </Grid>
            <Grid item xs={12}>
                <EventDeliveryLogList participant={row.id} enableAdminLinks={enableAdminLinks} enableCWLinks={enableCWLinks} />
            </Grid>
            <Grid item xs={12}>
                <RevisionList participant={row.id} />
            </Grid>
        </Grid>
    </div>);
}

const MobileTripRow = ({ row, idx, rowsLength }) => {
    const theme = useTheme();
    const { tagColor } = React.useContext(ContextStore);

    const tourName = row.tour?.name ?? '-';
    // CP-8349: a guide group's dates are synthetic -- label it instead.
    const startDate = row.tour?.is_staff_chat_group
        ? 'Guide group'
        : row.start_datetime && row.tour?.start_timezone
            ? moment.tz(row.start_datetime, row.tour.start_timezone).format('MMM DD, YYYY HH:mm z')
            : '-';

    return (
        <>
            <Grid container spacing={1} p={2}>
                <Grid item xs={12} container alignItems="center" justifyContent="space-between">
                    <Grid item xs>
                        <Link
                            href={`/trip/${row.tour?.id}/users/manager/${row.id}`}
                            target="_blank"
                            style={{ fontWeight: 500, textDecoration: 'none' }}
                        >
                            {tourName}
                        </Link>
                    </Grid>
                    <Grid item>
                        <BookingStatusChip value={row.booking_state} />
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                        {startDate}
                    </Typography>
                </Grid>

                {row.custom_id && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                            External ID: {row.custom_id}
                        </Typography>
                    </Grid>
                )}

                {row.tags && row.tags.length > 0 && (
                    <Grid item xs={12} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {row.tags.slice(0, 5).map((tag, tagIdx) => {
                            const color = tagColor(tag);
                            return (
                                <Chip
                                    key={tagIdx}
                                    label={tag.name}
                                    size="small"
                                    style={{
                                        backgroundColor: color,
                                        color: theme.palette.getContrastText(color),
                                    }}
                                />
                            );
                        })}
                        {row.tags.length > 5 && (
                            <Chip label={`+${row.tags.length - 5}`} size="small" variant="outlined" />
                        )}
                    </Grid>
                )}
            </Grid>
            {idx < rowsLength - 1 && <Divider />}
        </>
    );
};

const UserProfileTrips = (props) => {
    const userId = props.user.id;
    const isSmallScreen = !useIsWidthUp('sm');
    const { isCuratedPlanetUser, isAWSUser, hasPerm } = React.useContext(ContextStore);
    const enableAdminLinks = isCuratedPlanetUser || false;
    const enableCWLinks = isAWSUser || false;

    const classes = useStyles();
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const bgCtx = React.useContext(BackgroundTaskContext);

    const resource = 'tour_user';

    const [detailPanelRowIds] = React.useState([]);


    const getDetailPanelHeight = React.useCallback((p) => {
        return "auto";
    }, []);
    const getDetailPanelContent = React.useCallback((p) => {
        return <BookingDetailPanel row={p.row} enableAdminLinks={enableAdminLinks} enableCWLinks={enableCWLinks} />
    }, []);

    const dispatchBookingTask = React.useCallback(async (row, urlSuffix, errorLabel) => {
        try {
            const res = await dataAPI(CREATE, 'tour_user', {
                urlSuffix: `${row.id}/${urlSuffix}`,
                data: {},
            });
            if (isBackgroundTaskResponse(res)) {
                notifyBackgroundTask(res, enqueueSnackbar, closeSnackbar, bgCtx);
            } else if (res && res.statusCode >= 400) {
                enqueueSnackbar(res.data?.message || errorLabel, { variant: 'error' });
            } else {
                enqueueSnackbar(errorLabel, { variant: 'error' });
            }
        } catch (e) {
            enqueueSnackbar(errorLabel, { variant: 'error' });
        }
    }, [dataAPI, enqueueSnackbar, closeSnackbar, bgCtx]);

    const canRunIntegrationActions = hasPerm('ops', 'sync_bookings');

    const bookingActions = React.useMemo(() => {
        const noIntegration = (row) => row.integration
            ? null
            : 'No integration set for this booking';
        return [
            {
                key: 'reimport',
                label: 'Reimport',
                icon: <SyncIcon fontSize="small" />,
                hidden: () => !canRunIntegrationActions,
                disabledReason: noIntegration,
                onClick: (row) => dispatchBookingTask(row, 'reimport/', 'Error starting reimport'),
            },
            {
                key: 'fetch_artifacts',
                label: 'View API response',
                icon: <VisibilityIcon fontSize="small" />,
                hidden: () => !canRunIntegrationActions,
                disabledReason: noIntegration,
                onClick: (row) => dispatchBookingTask(row, 'fetch_artifacts/', 'Error starting fetch'),
            },
        ];
    }, [canRunIntegrationActions, dispatchBookingTask]);

    const columns = [
        {
            field: 'custom_id',
            headerName: 'External ID',
            flex: 1,
        },
        {
            field: 'tour_name',
            headerName: 'Tour Name',
            flex: 2,
            renderCell: (p) => <Link href={`/trip/${p.row.tour.id}/users/manager/${p.row.id}`} target="_blank">{p.row.tour?.name ?? '-'}</Link>,
        },
        {
            field: 'start_datetime',
            headerName: 'Date & Time',
            width: 180,
            type: 'dateTime',
            // CP-8349: a guide group's dates are synthetic -- never show one as expired.
            valueGetter: (p) => p.row.tour?.is_staff_chat_group ? null : moment.tz(p.row.start_datetime, p.row.tour.start_timezone).toDate(),
            renderCell: (p) => p.row.tour?.is_staff_chat_group
                ? <Typography variant="body2" fontSize="0.75rem" color="textSecondary">Guide group</Typography>
                : <DateTimeCell value={p.value} tz={p.row.tour.start_timezone} />,
        },
        {
            field: 'order_ref',
            headerName: 'Order #',
            flex: 1,
            valueGetter: (p) => p.row.order_ref,
        },
        {
            // The operator's code for the departure INSTANCE, not the
            // time-of-day departure tag.
            field: 'departure_key',
            headerName: 'Departure code',
            flex: 1,
            valueGetter: (p) => p.row.departure_key || '',
        },
        {
            field: 'supplier_ref',
            headerName: 'Supplier #',
            flex: 1,
            valueGetter: (p) => p.row.supplier_ref,
        },
        {
            field: 'reseller_ref',
            headerName: 'Reseller #',
            flex: 1,
            valueGetter: (p) => p.row.reseller_ref,
        },
        {
            field: 'booking_state',
            headerName: 'State',
            width: 100,
            filterable: true,
            type: 'singleSelect',
            valueOptions: bookingStatesGridOptions,
            renderCell: (p) => <BookingStatusChip value={p.value} />,
        },
        {
            field: 'tags',
            headerName: 'Tags',
            sortable: false,
            filterable: false,
            // width: 10,
            flex: 5,
            renderCell: (p) => <ColoredTagCell tags={p.row.tags} />,
        },
        {
            field: 'integration',
            headerName: 'Booking System',
            sortable: true,
            filterable: true,
            width: 220
        },
        {
            field: 'created_ts',
            headerName: 'Created at',
            sortable: true,
            filterable: false,
            width: 180,
            type: 'dateTime',
            // Revert type and valueGetter change
            valueGetter: (p) => moment.utc(p.row.created_ts).toDate(),
            renderCell: (p) => <DateTimeCell value={p.value} />,
        },
        {
            field: 'updated_ts',
            headerName: 'Updated at',
            sortable: true,
            filterable: false,
            width: 180,
            type: 'dateTime',
            // Revert type and valueGetter change
            valueGetter: (p) => moment.utc(p.row.updated_ts).toDate(),
            renderCell: (p) => <DateTimeCell value={p.value} />,
        },
        {
            field: 'booking_ts',
            headerName: 'Booking date',
            sortable: true,
            filterable: false,
            width: 180,
            type: 'dateTime',
            valueGetter: (p) => p.row.booking_ts ? moment.utc(p.row.booking_ts).toDate() : null,
            renderCell: (p) => <DateTimeCell value={p.value} />,
        },
        { field: 'lang', headerName: 'Language', sortable: false, filterable: true, flex: 0.5, valueGetter: (p) => p.row.user.lang },
        { field: 'departure', headerName: 'Departure', sortable: false, filterable: false, width: 100, valueGetter: (p) => p.row.departure?.name ?? '-' },

        {
            field: 'checkin_source',
            headerName: 'Check-in source',
            sortable: true,
            ilterable: true,
            type: 'singleSelect',
            valueOptions: checkinSourcesGridOptions,
            flex: 0.5,
            valueFormatter: (p) => typeof CHECKIN_SOURCES[p.value] !== "undefined" ? CHECKIN_SOURCES[p.value] : 'Not pre-checked-in'
        },
        {
            field: 'precheckin_status',
            headerName: 'Pre-check-in',
            sortable: false,
            filterable: false,
            type: 'singleSelect',
            valueOptions: precheckinStatusesGridOptions,
            flex: 0.5,
            valueFormatter: (p) => PRECHECKIN_STATUSES[p.value]
        },
        { field: 'survey_state', headerName: 'Survey', sortable: true, filterable: true, type: 'singleSelect', valueOptions: surveyStatesGridOptions, flex: 0.5, valueFormatter: (p) => SURVEY_STATES[p.value] },
        { field: 'mytrips_display', headerName: 'My Trips', sortable: true, filterable: true, type: 'boolean', flex: 0.5, valueGetter: (p) => p.row.mytrips_display },
        { field: 'exclude_from_chat', headerName: 'Chat disabled', sortable: true, filterable: true, type: 'boolean', flex: 0.5, valueGetter: (p) => p.row.exclude_from_chat },
        { field: 'exclude_from_group_chat', headerName: 'Group Chat disabled', sortable: true, filterable: true, type: 'boolean', flex: 0.5, valueGetter: (p) => p.row.exclude_from_group_chat },
        { field: 'voucher', headerName: 'Voucher', sortable: false, filterable: false, flex: 0.5, renderCell: (p) => (p.row.voucher ? <Link href={p.row.voucher.file} target="_blank">{p.row.voucher.name}</Link> : '') },
        { field: 'affiliate_ext_id', headerName: 'Affiliate booking ID', sortable: true, filterable: true, flex: 0.5, valueGetter: (p) => p.row.affiliate_ext_id },
        { field: 'people_count', headerName: 'People count', sortable: true, filterable: true, flex: 0.5, valueGetter: (p) => p.row.people_count || p.row.tag_context?.people_count },
        { field: 'int_ids', headerName: 'Internal IDs', sortable: true, filterable: false, flex: 0.5, valueGetter: (p) => `${p.row.id}/${p.row.itinerary}/${p.row.public_id}` },
        {
            field: 'deep_link', headerName: 'Deep link', sortable: false, filterable: false, flex: 0.5,
            valueGetter: (p) => p.row.branchio_link,
            renderCell: (p) => <>{p.value && <><Link href={p.value} target="_blank">{p.value}</Link> <OpenInNewIcon fontSize="x-small" /></>}</>,
        },
        {
            field: 'main_booking', headerName: 'Main booking', sortable: false, filterable: false, flex: 0.5,
            renderCell: (p) => (p.row.main_booking ? <Link href={`/trip/${p.row.tour.id}/users/manager/${p.row.main_booking?.id}`} target="_blank">{p.row.main_booking?.display_name}</Link> : null),
        },
        {
            field: 'create_source', headerName: 'Registration method', type: 'singleSelect', sortable: true, filterable: true, valueOptions: sourcesGridOptions,
            valueGetter: (p) => (p.row?.create_source),
            valueFormatter: (p) => (SOURCES[+p.value] || 'Unknown'),
        },
        // Revert update_source changes
        {
            field: 'update_source', headerName: 'Update method', type: 'singleSelect', sortable: true, filterable: true, valueOptions: sourcesGridOptions,
            valueGetter: (p) => p.row.update_source,
            valueFormatter: (p) => SOURCES[+p.value] || 'Unknown'
        },
        {
            field: 'pickup_time', headerName: 'Pickup time', sortable: false, filterable: false, flex: 0.5, renderCell: (p) => (
                p.row.tag_context?.pickupDateTime ? moment.utc(p.row.tag_context.pickupDateTime).format("h:mm a") : null
            )
        },
        {
            field: 'actions',
            headerName: '',
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            disableExport: true,
            width: 56,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => <BookingActionsMenu row={p.row} actions={bookingActions} />,
        },
    ];

    const initialState = {
        detailPanel: {
            expandedRowIds: detailPanelRowIds,
        },
        sorting: {
            sortModel: [{ field: 'start_datetime', sort: 'desc' }],
        },
        filter: {
            filterModel: {
                items: [
                    { field: 'booking_state', operatorValue: 'is', value: [BOOKING_STATE_ON_HOLD, BOOKING_STATE_BOOKED, BOOKING_STATE_CANCELLED, BOOKING_STATE_CANCELLED_ON_HOLD] },
                ]
            }
        },
        columns: {
            columnVisibilityModel: {
                date: false,
                integration: false,
                // created_ts: false,
                updated_ts: false,
                booking_ts: false,
                lang: false,
                departure: false,
                deep_link: false,

                survey_state: false,
                mytrips_display: false,
                exclude_from_chat: false,
                exclude_from_group_chat: false,
                voucher: false,
                affiliate_ext_id: false,
                people_count: false,
                int_ids: false,
                main_booking: false,
                // create_source: false,
                update_source: false,
                pickup_time: false,
                precheckin_status: false,
            }
        }
    }

    if (!userId) {
        return (
            <Grid container justifyContent="center" alignItems="center" style={{ minHeight: 100 }}>
                <Typography variant="body1">No user specified.</Typography>
            </Grid>
        );
    }

    return (
        <div className={classes.root} style={props.style}>
            <Grid xs={12} item container direction="row">
                <Grid item xs={12} className={classes.recordListWrapper} mx={isSmallScreen ? 0 : 0} px={isSmallScreen ? 1 : 0}>
                    <RecordListPro
                        title="Trips"
                        className={classes.recordList}
                        resource={resource}
                        apiFilters={[{ field: 'user_ids', value: `${userId}` }]}
                        columns={columns}
                        // filters={filters}
                        pageSize={25}
                        autoHeight
                        disableRowSelectionOnClick
                        disableSelectionOnClick
                        initialState={initialState}
                        getDetailPanelHeight={getDetailPanelHeight}
                        getDetailPanelContent={getDetailPanelContent}
                        mobileRowComponent={MobileTripRow}
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default UserProfileTrips;
